import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Text, Button, SelectInput, TextInput, DateInput, StatusBadge} from '@/ui-kits';
import {DataTable} from '@/components';
import {HospitalSpecialistAssignmentStatus, HospitalSpecialistAssignmentStatusBadgeVariants, HospitalSpecialistAssignmentStatusLabels, NA, StatusBadgeVariant} from '@/constants';
import {
  SelectInputitem,
  DataTableColumn,
  GetHospitalConsultationCaseListRequestPayload,
  HospitalConsultationCase,
  AssignHospitalSpecialistToConsultationRequestPayload,
} from '@/interface';
import {
  getHospitalConsultationCaseListRequest,
  assignHospitalSpecialistToConsultationRequest,
  getHospitalSpecialistAllRequest,
  resetHospitalAdminMessage,
} from '@/services/redux/slices';
import {
  hospitalAdminLoadingSelector,
  hospitalAdminConsultationCaseListSelector,
  hospitalAdminConsultationCaseListCurrentPageSelector,
  hospitalAdminConsultationCaseListTotalPagesSelector,
  hospitalAdminConsultationCaseListTotalResutltsSelector,
  hospitalAdminSuccessSelector,
  hospitalAdminFailureSelector,
  hospitalPocProfileSpecializationsSelector,
  hospitalSpecialistAll,
} from '@/services/redux/selectors';
import {ErrorCodes, getErrorMessage, SuccessCodes, getSuccessMessage} from '@/utils/getMessages';
import {useToast, useConfimationModal} from '@/hooks';
import {formatPatientAdminConsulationDate, formatName, enumToSelectOptions, formatDate} from '@/utils/common_functions';

// Status filter options (0 = Pending, 1 = Assigned)
const StatusFilterOptions: SelectInputitem[] = enumToSelectOptions(HospitalSpecialistAssignmentStatus);

interface HospitalPocConsulationProps {}

export const HospitalPocConsulation: React.FC<HospitalPocConsulationProps> = () => {
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {showModal} = useConfimationModal();

  // ==========================
  // selectors
  // ==========================
  const isLoading = useSelector(hospitalAdminLoadingSelector);
  const success = useSelector(hospitalAdminSuccessSelector) as SuccessCodes;
  const failure = useSelector(hospitalAdminFailureSelector) as ErrorCodes;
  const specializationList = useSelector(hospitalPocProfileSpecializationsSelector);
  const consultationCases = useSelector(hospitalAdminConsultationCaseListSelector);
  const currentPage = useSelector(hospitalAdminConsultationCaseListCurrentPageSelector);
  const totalPages = useSelector(hospitalAdminConsultationCaseListTotalPagesSelector);
  const totalResults = useSelector(hospitalAdminConsultationCaseListTotalResutltsSelector);
  const hospitalSpecialistsAll = useSelector(hospitalSpecialistAll);

  // ==========================
  // state
  // ==========================
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<HospitalSpecialistAssignmentStatus | null>(null);
  const [specializationFilter, setSpecializationFilter] = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const [page, setPage] = useState<number>(1);
  const [selectedConsultationId, setSelectedConsultationId] = useState<number | null>(null);
  const [selectedSpecialistId, setSelectedSpecialistId] = useState<number | null>(null);
  const [isFilterActive, setIsFilterActive] = useState<boolean>();
  const filteredSpecialization = useMemo(() => {
    return specializationList.filter(specialization => specialization.status);
  }, [specializationList]);

  // ==========================
  // functions
  // ==========================
  const fetchConsultationCaseList = useCallback(() => {
    const payload: GetHospitalConsultationCaseListRequestPayload = {
      page,
      ...(searchText && {search_text: searchText}),
      ...(statusFilter !== null && {status: statusFilter}),
      ...(specializationFilter && {specialization: specializationFilter}),
      ...(dateFilter && {date: dateFilter.toISOString()}),
    };

    setIsFilterActive(Boolean(searchText || statusFilter !== null || specializationFilter !== null || dateFilter !== null));
    dispatch(getHospitalConsultationCaseListRequest(payload));
  }, [searchText, statusFilter, specializationFilter, dateFilter, page]);

  const getFilteredSpecialists = useCallback(
    (specializationId: number): SelectInputitem[] => {
      if (!hospitalSpecialistsAll || hospitalSpecialistsAll.length === 0) {
        return [];
      }
      return hospitalSpecialistsAll
        .filter((spec: any) => spec.specialization === specializationId)
        .map((spec: any) => ({
          id: spec.id,
          label: spec.name,
        }));
    },
    [hospitalSpecialistsAll],
  );

  const handleSpecialistSelect = useCallback(
    (consultationId: number, specialistId: number, specialistName: string) => {
      setSelectedConsultationId(consultationId);
      setSelectedSpecialistId(specialistId);
      // Show confirmation modal
      showModal({
        header: `You are about to assign Dr. ${formatName(specialistName)}\nto this consultation case. `,
        onConfirmHandler: () => {
          const payload: AssignHospitalSpecialistToConsultationRequestPayload = {
            id: consultationId,
            specialist_id: specialistId,
          };
          dispatch(assignHospitalSpecialistToConsultationRequest(payload));
        },
        primaryCta: 'confirm',
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
      });
    },
    [dispatch, showModal],
  );

  const handleResetFilters = () => {
    setSearchText('');
    setStatusFilter(null);
    setSpecializationFilter(null);
    setDateFilter(null);
    setPage(1);
    setIsFilterActive(false);
  };

  // ==========================
  // side effects
  // ==========================
  useEffect(() => {
    const timeout = setTimeout(fetchConsultationCaseList, 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [fetchConsultationCaseList]);

  useEffect(() => {
    // Fetch hospital specialist list on mount
    dispatch(getHospitalSpecialistAllRequest());
  }, []);
  useEffect(() => {
    if (success) {
      if (success === 'S-10098') {
        showToast(getSuccessMessage(success), 'success');
        fetchConsultationCaseList();
      }
    }
    if (failure) {
      if (!['E-10054', 'E-10044', 'E-10177'].includes(failure)) {
        showToast(getErrorMessage(failure), 'failure');
      }
    }
    return () => {
      dispatch(resetHospitalAdminMessage());
    };
  }, [success, failure]);

  // ==========================
  // columns
  // ==========================
  const columns: DataTableColumn<HospitalConsultationCase>[] = useMemo(
    () => [
      {
        name: 'id',
        align: 'center',
        title: <Text className="!font-FigtreeBold">CASE ID</Text>,
        render: row => `#${row.id}`,
        width: {minWidth: '100px', maxWidth: '150px'},
      },
      {
        name: 'patient_name',
        align: 'left',
        title: <Text className="!font-FigtreeBold">PATIENT NAME</Text>,
        render: row => <Text className="break-words text-wrap">{formatName(row.patient_name)}</Text>,
        width: {minWidth: '180px', maxWidth: '250px'},
      },
      {
        name: 'specialization',
        align: 'left',
        title: <Text className="!font-FigtreeBold">SPECIALIZATION</Text>,
        render: row => <Text className="break-words text-wrap">{row.specialization?.label || NA}</Text>,
        width: {minWidth: '250px', maxWidth: '500px'},
      },
      {
        name: 'created_at',
        align: 'center',
        title: <Text className="!font-FigtreeBold">BOOKING DATE</Text>,
        render: row => <Text>{row.created_at ? formatDate(row.created_at, 'yyyy-MMM-dd, hh:mm:ss') : NA}</Text>,
        width: {minWidth: '150px', maxWidth: '200px'},
      },
      {
        name: 'specialist',
        align: 'left',
        title: <Text className="!font-FigtreeBold">ASSIGNED SPECIALIST</Text>,
        render: row => {
          if (row.specialist) {
            return <Text className="break-words text-wrap">Dr. {formatName(row.specialist.name)}</Text>;
          }
          // Show dropdown when no specialist is assigned
          const filteredSpecialists = getFilteredSpecialists(row.specialization?.id);
          if (filteredSpecialists.length === 0) {
            return <Text className="text-ui_input_border_dark/60">No specialists available</Text>;
          }
          if (row.status === HospitalSpecialistAssignmentStatus.Cancelled) {
            return <Text className="">Case Cancelled</Text>;
          }
          return (
            <SelectInput
              value={null}
              options={filteredSpecialists}
              onChange={item => handleSpecialistSelect(row.id, item.id as number, item.label as string)}
              placeholder="Select Specialist"
              wrapperClassName="min-w-[200px] border"
              className="min-w-[200px]"
              usePortal
            />
          );
        },
        width: {minWidth: '230px', maxWidth: '300px'},
      },
      {
        name: 'status',
        align: 'center',
        title: <Text className="!font-FigtreeBold">STATUS</Text>,
        render: row => (
          <StatusBadge variant="secondary" status={HospitalSpecialistAssignmentStatusBadgeVariants[row.status]} className="py-1">
            {HospitalSpecialistAssignmentStatusLabels[row.status]}
          </StatusBadge>
        ),
        width: {minWidth: '120px', maxWidth: '150px'},
      },
    ],
    [getFilteredSpecialists, handleSpecialistSelect],
  );

  return (
    <div className="p-2 sm:py-4 md:p-6 flex flex-col grow gap-4">
      <Text variant="heading" className="text-ui_blue mb-4">
        Consultation list
      </Text>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 z-[100]">
        <TextInput
          value={searchText}
          onChangeText={setSearchText}
          leftIcon="search_blue"
          className="sm:max-w-[300px] w-full col-span-full"
          wrapperClassName="px-2 border"
          placeholder="Search by Case ID, Patient name"
        />
        <SelectInput
          value={statusFilter !== null ? statusFilter : null}
          onChange={value => setStatusFilter(value.id as number)}
          className="sm:max-w-[200px] w-full"
          itemTextClassName="text-wrap !text-sm"
          placeholder="Status"
          wrapperClassName="border"
          options={StatusFilterOptions}
        />
        <SelectInput
          value={specializationFilter || ''}
          onChange={value => setSpecializationFilter(value.id as number)}
          className="sm:max-w-[200px] w-full"
          itemTextClassName="text-wrap !text-sm"
          placeholder="Specialization"
          wrapperClassName="border"
          options={filteredSpecialization}
        />
        <DateInput
          value={dateFilter}
          onChangeDate={setDateFilter}
          placeholder="Date"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          textClassName="text-start text-ui_blue"
          maxValue={new Date()}
        />
        {isFilterActive && <Button title="Reset Filters" variant="secondary" onClick={handleResetFilters} />}
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={consultationCases}
        totalPages={totalPages}
        currentPage={currentPage}
        totalResult={totalResults}
        onPageChange={setPage}
        loading={isLoading}
        errorMessage={consultationCases.length === 0 && !isLoading ? 'No consultation cases found' : undefined}
      />
    </div>
  );
};
